import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Send, ShieldCheck, Loader2 } from "lucide-react";
import { Turnstile } from "@marsidev/react-turnstile";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Reveal, SectionLabel } from "../../lib/motion";
import { PROPERTY, CTA_LABEL } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Turnstile site key is PUBLIC (domain-locked by Cloudflare server-side), so it is safe
// to embed. We prefer the env var but fall back to the real key so the widget always
// renders on production even if the build didn't inject REACT_APP_TURNSTILE_SITE_KEY.
const REAL_TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAD3P81-QYxrpjWxE";
// Preview/localhost use Cloudflare's always-pass test key so the form stays testable there;
// every other (production) host uses the real domain-bound site key.
const DEV_HOST_RE = /(^localhost$)|(^127\.0\.0\.1$)|(\.preview\.emergentagent\.com$)/i;
const isDevHost =
  typeof window !== "undefined" && DEV_HOST_RE.test(window.location.hostname);
const TURNSTILE_SITE_KEY = isDevHost
  ? "1x00000000000000000000AA"
  : REAL_TURNSTILE_SITE_KEY;

export const Contact = ({ inquiryType, resetInquiryType }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const isConversation = inquiryType === "conversation";

  useEffect(() => {
    const prefill = {
      due_diligence: "I am interested in Bay Quarter Shores Lot 17-L currently offered at $16,900. Please send me the complete Due Diligence Package and any additional information about the property.",
      conversation: "I would like to schedule a conversation about Bay Quarter Shores Lot 17-L.",
    }[inquiryType];
    if (prefill) setForm((f) => ({ ...f, message: f.message || prefill }));
  }, [inquiryType]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please provide your name and email.");
      return;
    }
    if (!consent) {
      toast.error("Please acknowledge the independent due diligence statement.");
      return;
    }
    if (!turnstileToken) {
      toast.error("Please complete the \u201CVerify you are human\u201D check below, then submit.");
      // nudge the widget to render/execute its challenge
      turnstileRef.current?.execute?.();
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/inquiries`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        inquiry_type: inquiryType || "due_diligence",
        consent,
        company, // honeypot (should be empty)
        turnstile_token: turnstileToken,
        referrer: typeof document !== "undefined" ? document.referrer : "",
      });
      const delivered = res?.data?.email_sent !== false;
      if (delivered) {
        toast.success("Thank you. Your request has been received successfully.", {
          description: "The seller has been notified and will contact you shortly regarding the Due Diligence Package.",
        });
      } else {
        toast.warning("Your request was received and saved.", {
          description: "We hit a temporary email delivery issue, but the seller has your details and we'll follow up shortly.",
        });
      }
      setForm({ name: "", email: "", phone: "", message: "" });
      setConsent(false);
      setTurnstileToken("");
      turnstileRef.current?.reset();
      resetInquiryType?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) {
        toast.error("Too many requests. Please try again in a few minutes.");
      } else if (status === 400) {
        toast.error(err?.response?.data?.detail || "Human verification failed. Please try again.");
        setTurnstileToken("");
        turnstileRef.current?.reset();
      } else if (status === 422) {
        toast.error(err?.response?.data?.detail || "Please check your details and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-warm-light/50 scroll-mt-20">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <SectionLabel>{isConversation ? "Schedule a Conversation" : "Request the Package"}</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl font-light text-navy mt-4 leading-tight">
              Request the Due Diligence Package
            </h2>
            <p className="mt-6 text-warm leading-relaxed">
              The seller has assembled extensive documentation to assist prospective buyers. Complete
              the form to request the Due Diligence Package or ask questions about the property.
            </p>

            <div className="mt-10 space-y-4">
              {[
                "Complete, verified seller due diligence records",
                "Transparent disclosure of all findings",
                "A secure link delivered directly to you",
              ].map((t) => (
                <div key={t} className="flex items-center gap-3 text-navy/90">
                  <div className="w-8 h-8 rounded-full bg-forest/10 flex items-center justify-center shrink-0">
                    <ShieldCheck size={16} className="text-forest" />
                  </div>
                  <span className="text-sm">{t}</span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <motion.form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="bg-white border border-warm-border rounded-3xl p-8 shadow-[0_24px_70px_-28px_rgba(11,37,69,0.3)]"
            >
              <div data-testid="contact-asking-price" className="mb-5 flex items-center justify-between gap-3 bg-water-pale/70 border border-water/15 rounded-2xl px-5 py-3">
                <span className="label-caps text-[10px] text-water">Current Asking Price</span>
                <span className="font-serif text-xl text-navy leading-none">{PROPERTY.price}</span>
              </div>
              <div className="space-y-5">
                <div aria-hidden="true" className="absolute left-[-9999px] top-[-9999px] h-0 w-0 overflow-hidden" tabIndex={-1}>
                  <label htmlFor="company">Company (leave blank)</label>
                  <input
                    id="company"
                    name="company"
                    type="text"
                    autoComplete="off"
                    tabIndex={-1}
                    data-testid="contact-honeypot"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="name" className="text-navy">Name</Label>
                  <Input id="name" data-testid="contact-name-input" value={form.name} onChange={update("name")} placeholder="Your full name" className="mt-2 h-12 rounded-xl" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="email" className="text-navy">Email</Label>
                    <Input id="email" type="email" data-testid="contact-email-input" value={form.email} onChange={update("email")} placeholder="you@email.com" className="mt-2 h-12 rounded-xl" />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-navy">Phone</Label>
                    <Input id="phone" data-testid="contact-phone-input" value={form.phone} onChange={update("phone")} placeholder="(optional)" className="mt-2 h-12 rounded-xl" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-navy">Message</Label>
                  <Textarea id="message" data-testid="contact-message-input" value={form.message} onChange={update("message")} placeholder="Tell us what you'd like to know..." rows={5} className="mt-2 rounded-xl resize-none" />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    id="consent"
                    data-testid="contact-consent-checkbox"
                    checked={consent}
                    onCheckedChange={(v) => setConsent(Boolean(v))}
                    className="mt-0.5"
                  />
                  <span className="text-sm text-warm leading-snug">
                    I understand that I am solely responsible for conducting my own independent due diligence before deciding to purchase this property.
                  </span>
                </label>

                {TURNSTILE_SITE_KEY && (
                  <div data-testid="contact-turnstile">
                    <Turnstile
                      ref={turnstileRef}
                      siteKey={TURNSTILE_SITE_KEY}
                      onSuccess={(token) => {
                        console.log("[turnstile] success — token length:", token ? token.length : 0);
                        setTurnstileToken(token);
                      }}
                      onExpire={() => {
                        console.log("[turnstile] token expired — cleared");
                        setTurnstileToken("");
                        turnstileRef.current?.reset();
                      }}
                      onError={(err) => {
                        console.log("[turnstile] error:", err);
                        setTurnstileToken("");
                      }}
                      options={{ theme: "light", appearance: "always" }}
                    />
                    {!turnstileToken && (
                      <p className="mt-2 text-xs text-warm" data-testid="turnstile-hint">
                        Please complete the &ldquo;Verify you are human&rdquo; check above to enable submission.
                      </p>
                    )}
                  </div>
                )}

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting || !turnstileToken}
                  className="w-full flex items-center justify-center gap-2 bg-water text-white rounded-full px-8 py-3.5 font-medium hover:bg-water-soft transition-colors shadow-sm hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed focus-visible:ring-2 focus-visible:ring-water focus-visible:outline-none"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
                  {CTA_LABEL}
                </button>
              </div>
            </motion.form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
